import React, { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, ConfirmDialog, EmptyState, StatusBadge } from '../ui';
import type {
  Skill,
  SkillListItem,
  SkillPreview,
} from '../../lib/api/skills';
import {
  MAX_SKILL_FILE_BYTES,
  MAX_TOTAL_SKILL_BYTES,
} from '../../../../shared/types/skills';

/**
 * Shared BYOS UI surface. Used by both the project-scoped settings
 * tab and the repo-scoped /repos/:org/:repo/skills page; the parent
 * passes API closures (list/upload/preview/toggle/delete) so the
 * component itself stays scope-agnostic.
 *
 * Render contract:
 *  - usage indicator at the top ("3 skills, 42 KB of 5 MB used")
 *  - drag-and-drop zone (+ file picker fallback)
 *  - skill cards (one per row) with toggle, preview, delete
 *  - project-scope rows in the repo view are tagged "Project default";
 *    overridden rows (project entry shadowed by a repo entry) are tagged "Overridden"
 *  - rows the caller marked `readOnly: true` hide delete/toggle
 */
export interface SkillsManagerApi {
  /** Cache key segment for tanstack-query — must be stable across renders. */
  scopeKey: string[];
  list: () => Promise<{ skills: SkillListItem[]; totalBytes: number; capBytes: number }>;
  upload: (file: File, opts: { name?: string; description?: string }) => Promise<Skill>;
  preview: (item: SkillListItem) => Promise<SkillPreview>;
  toggle: (item: SkillListItem, active: boolean) => Promise<Skill>;
  remove: (item: SkillListItem) => Promise<void>;
  /** Optional: render a hint that a row belongs to the parent project. */
  rowReadOnly?: (item: SkillListItem) => boolean;
  /** Optional: parent CTA shown above the upload zone (e.g. "Go to project settings"). */
  banner?: React.ReactNode;
}

export const SkillsManager: React.FC<SkillsManagerApi> = (api) => {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['skills', ...api.scopeKey],
    queryFn: () => api.list(),
  });
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<SkillListItem | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SkillListItem | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) => api.upload(file, {}),
    onSuccess: () => {
      setUploadError(null);
      qc.invalidateQueries({ queryKey: ['skills', ...api.scopeKey] });
    },
    onError: (err: Error) => setUploadError(err.message),
  });

  const toggle = useMutation({
    mutationFn: (args: { item: SkillListItem; active: boolean }) =>
      api.toggle(args.item, args.active),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['skills', ...api.scopeKey] }),
  });

  const remove = useMutation({
    mutationFn: (item: SkillListItem) => api.remove(item),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ['skills', ...api.scopeKey] });
    },
  });

  const data = q.data;
  const usage = useMemo(() => {
    if (!data) return null;
    const used = data.totalBytes;
    const cap = data.capBytes || MAX_TOTAL_SKILL_BYTES;
    const pct = cap === 0 ? 0 : Math.min(100, Math.round((used / cap) * 100));
    return {
      count: data.skills.length,
      used,
      cap,
      pct,
      pretty: `${data.skills.length} ${data.skills.length === 1 ? 'skill' : 'skills'}, ${formatBytes(used)} of ${formatBytes(cap)} used`,
    };
  }, [data]);

  return (
    <div className="space-y-4">
      {api.banner}

      {usage && (
        <div className="flex items-center justify-between text-xs text-slate-600">
          <div>{usage.pretty}</div>
          <div className="w-48 h-1.5 bg-slate-100 rounded-full overflow-hidden" aria-hidden>
            <div
              className={
                'h-full ' +
                (usage.pct >= 90 ? 'bg-danger-500' : usage.pct >= 70 ? 'bg-warning-500' : 'bg-primary-500')
              }
              style={{ width: `${usage.pct}%` }}
            />
          </div>
        </div>
      )}

      <UploadZone
        onPick={(file) => upload.mutate(file)}
        disabled={upload.isPending}
        errorText={uploadError}
      />

      {q.isLoading && <div className="text-sm text-slate-500">Loading skills…</div>}
      {q.isError && (
        <div role="alert" className="text-sm text-danger-600">
          {(q.error as Error).message}
        </div>
      )}
      {!q.isLoading && data && data.skills.length === 0 && (
        <EmptyState
          title="No skills yet"
          description="Upload a .md or .zip file describing your team's coding patterns. Every Forge agent will read it before writing code."
        />
      )}

      {data && data.skills.length > 0 && (
        <ul className="space-y-2" data-testid="skills-list">
          {data.skills.map((item) => {
            const readOnly = api.rowReadOnly?.(item) ?? false;
            return (
              <li
                key={`${item.parentPk}#${item.slug}`}
                data-testid="skill-card"
                className={
                  'flex items-start gap-3 p-3 rounded-md border border-slate-200 bg-white ' +
                  (item.active ? '' : 'opacity-60')
                }
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-slate-900 truncate">{item.name}</div>
                    <ScopeBadge scope={item.scope} overridden={item.overridden} />
                    {item.kind === 'bundle' && (
                      <StatusBadge tone="neutral">bundle</StatusBadge>
                    )}
                    {!item.active && <StatusBadge tone="warning">Not injected</StatusBadge>}
                  </div>
                  {item.description && (
                    <div className="text-xs text-slate-500 mt-0.5 truncate">{item.description}</div>
                  )}
                  <div className="text-[11px] text-slate-400 mt-1 font-mono">
                    {item.slug} · {formatBytes(item.sizeBytes)} · uploaded {formatDate(item.uploadedAt)}
                    {item.uploadedBy ? ` by ${item.uploadedBy}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => setPreviewing(item)}>
                    Preview
                  </Button>
                  {!readOnly && (
                    <>
                      <ToggleSwitch
                        active={item.active}
                        onChange={(active) =>
                          toggle.mutate({ item, active })
                        }
                        loading={toggle.isPending}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-danger-600 hover:bg-danger-50"
                        onClick={() => setConfirmDelete(item)}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {previewing && (
        <PreviewModal
          item={previewing}
          load={() => api.preview(previewing)}
          onClose={() => setPreviewing(null)}
        />
      )}
      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete skill?"
        description={
          confirmDelete && (
            <>
              Remove <span className="font-mono text-slate-900">{confirmDelete.slug}</span>?
              The next Forge agent launch on this {confirmDelete.scope} will no longer see it.
            </>
          )
        }
        confirmLabel="Delete"
        tone="danger"
        pending={remove.isPending}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
        testId="skills-delete-confirm"
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Drag-and-drop upload zone
// ---------------------------------------------------------------------------

const UploadZone: React.FC<{
  onPick: (file: File) => void;
  disabled: boolean;
  errorText: string | null;
}> = ({ onPick, disabled, errorText }) => {
  const [over, setOver] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const validateAndPick = (file: File): void => {
    setPickError(null);
    if (!/\.(md|zip)$/i.test(file.name)) {
      setPickError('Only .md and .zip files are supported.');
      return;
    }
    if (file.size > MAX_SKILL_FILE_BYTES) {
      setPickError(`File is too large (${formatBytes(file.size)}). Limit: ${formatBytes(MAX_SKILL_FILE_BYTES)}.`);
      return;
    }
    onPick(file);
  };

  return (
    <div>
      <div
        data-testid="skills-upload-zone"
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          if (disabled) return;
          const file = e.dataTransfer.files?.[0];
          if (file) validateAndPick(file);
        }}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        className={
          'border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ' +
          (over
            ? 'border-primary-500 bg-primary-50'
            : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50') +
          (disabled ? ' opacity-50 pointer-events-none' : '')
        }
      >
        <div className="text-sm text-slate-700 font-medium">
          {disabled ? 'Uploading…' : 'Drop a .md or .zip file here'}
        </div>
        <div className="text-xs text-slate-500 mt-1">
          or click to pick a file · 512 KB max per skill
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".md,.zip"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) validateAndPick(file);
            // Reset so re-picking the same file fires onChange again.
            e.target.value = '';
          }}
        />
      </div>
      {(pickError || errorText) && (
        <div role="alert" className="mt-2 text-xs text-danger-600">
          {pickError ?? errorText}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Active toggle (visual switch)
// ---------------------------------------------------------------------------

const ToggleSwitch: React.FC<{
  active: boolean;
  loading: boolean;
  onChange: (active: boolean) => void;
}> = ({ active, loading, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={active}
    aria-label={active ? 'Disable skill' : 'Enable skill'}
    disabled={loading}
    onClick={() => onChange(!active)}
    className={
      'relative inline-flex h-5 w-9 items-center rounded-full transition-colors ' +
      (active ? 'bg-primary-500' : 'bg-slate-300') +
      (loading ? ' opacity-50' : '')
    }
  >
    <span
      className={
        'inline-block h-4 w-4 transform rounded-full bg-white transition-transform ' +
        (active ? 'translate-x-4' : 'translate-x-0.5')
      }
    />
  </button>
);

// ---------------------------------------------------------------------------
// Preview + delete modals
// ---------------------------------------------------------------------------

const PreviewModal: React.FC<{
  item: SkillListItem;
  load: () => Promise<SkillPreview>;
  onClose: () => void;
}> = ({ item, load, onClose }) => {
  const q = useQuery({
    queryKey: ['skill-preview', item.parentPk, item.slug],
    queryFn: load,
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/45 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Skill preview"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate">{item.name}</div>
            <div className="text-xs text-slate-500 font-mono truncate">{item.slug}</div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </header>
        <div className="overflow-auto flex-1 p-5 space-y-4">
          {q.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
          {q.isError && (
            <div role="alert" className="text-sm text-danger-600">
              {(q.error as Error).message}
            </div>
          )}
          {q.data && (
            <>
              <pre className="text-xs leading-relaxed whitespace-pre-wrap break-words font-mono bg-slate-50 border border-slate-200 rounded-md p-3 max-h-[55vh] overflow-auto">
                {q.data.content}
              </pre>
              {q.data.bundleFiles.length > 0 && (
                <section>
                  <div className="text-xs font-semibold text-slate-700 mb-1">
                    Bundle scripts ({q.data.bundleFiles.length})
                  </div>
                  <ul className="text-xs font-mono space-y-0.5 text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3">
                    {q.data.bundleFiles.map((f) => (
                      <li key={f.relPath}>
                        <span className="text-slate-500">.forge/skills/{item.slug}/</span>
                        {f.relPath}
                        <span className="ml-2 text-slate-400">
                          {formatBytes(new Blob([f.content]).size)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ScopeBadge: React.FC<{ scope: 'project' | 'repo'; overridden: boolean }> = ({
  scope,
  overridden,
}) =>
  scope === 'project' ? (
    overridden ? (
      <StatusBadge tone="warning">Overridden</StatusBadge>
    ) : (
      <StatusBadge tone="info">Project</StatusBadge>
    )
  ) : (
    <StatusBadge tone="neutral">Repo</StatusBadge>
  );

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

export default SkillsManager;
